// First-run tour orchestrator. Owns the step machine, arms/disarms
// the first-edit highlight in the editor, and advances on the signals
// the tour is teaching:
//
//   edit-number  → heard-change   the buffer diverged from the demo
//                                  while audio was playing (the
//                                  hot-swap makes it audible ≤250ms
//                                  later — that IS the epiphany)
//   heard-change → meet-agent     the user clicks NEXT
//   meet-agent   → done           the user fires the suggested
//                                  agent prompt (or dismisses)
//
// Mounted once in App while the tour is armed; stays mounted across
// home ↔ editor swaps so step state survives, and renders nothing
// while the editor isn't showing. Esc or ✕ at any beat ends the tour
// for good — markOnboarded() persists on every path to 'done'.

import { useCallback, useEffect, useState } from 'react';
import type { EditorView } from '@codemirror/view';
import { Coachmark } from './Coachmark';
import { setFirstEditTarget } from './firstEditTarget';
import { markOnboarded, nextStep, type TourEvent, type TourStep } from './tourState';

// The one-click introduction to the agent. Deliberately a musical,
// song-aware ask — the agent mutates the running code rather than
// generating from scratch, and this prompt shows exactly that.
export const AGENT_INTRO_PROMPT =
  'Add a counter-melody that answers the lead.';

type Props = {
  /** True while the editor view is the visible surface. The tour
   *  hides (but keeps its step) when the user visits Home mid-tour. */
  visible: boolean;
  /** Current editor buffer. */
  code: string;
  /** The buffer the tour started from — divergence means "you edited". */
  demoCode: string;
  /** True while the engine is audibly playing. */
  playing: boolean;
  /** Live CodeMirror view, or null before SourcePane mounts. */
  view: EditorView | null;
  /** Pipe a prompt into the ChatPanel's send(). */
  onSeedAgentPrompt: (prompt: string) => void;
  /** Fired once when the tour reaches 'done' by any path. */
  onFinished: () => void;
};

export function OnboardingTour({
  visible,
  code,
  demoCode,
  playing,
  view,
  onSeedAgentPrompt,
  onFinished,
}: Props) {
  // The component only mounts when the user takes the Jump-in CTA, so
  // the machine starts at its first beat rather than 'idle'.
  const [step, setStep] = useState<TourStep>(() => nextStep('idle', 'begin'));

  // Reads `step` from the closure rather than a functional updater:
  // every caller (effects, coachmark buttons) re-binds per step, so
  // the closure is never stale — and the done-edge side effects stay
  // out of the updater, where StrictMode would double-run them.
  const fire = useCallback(
    (event: TourEvent) => {
      const next = nextStep(step, event);
      if (next === 'done' && step !== 'done') {
        markOnboarded();
        onFinished();
      }
      setStep(next);
    },
    [step, onFinished],
  );

  // Arm the phosphor pulse on the first-edit beat; disarm on every
  // other step. Re-runs when the view remounts (home ↔ editor swap)
  // so a fresh EditorView gets re-decorated. The cleanup disarms on
  // unmount too — a dismiss mid-beat unmounts this component in the
  // same batch that moves the step, so without it the pulse would
  // outlive the tour.
  useEffect(() => {
    if (!view) return;
    setFirstEditTarget(view, step === 'edit-number' && visible);
    return () => setFirstEditTarget(view, false);
  }, [view, step, visible]);

  // The taught moment: an edit that lands while the loop is running.
  // The hot-swap effect in App makes it audible on the next cycle —
  // this effect just names what happened.
  useEffect(() => {
    if (step !== 'edit-number') return;
    if (!playing) return;
    if (code === demoCode) return;
    fire('edited-while-playing');
  }, [step, playing, code, demoCode, fire]);

  // Esc dismisses from any beat. Listening only while a coachmark is
  // up keeps the handler out of the way of App's other Esc bindings
  // (solo-clear, palette) — those still receive the event; dismissing
  // a tour is not exclusive.
  useEffect(() => {
    if (step === 'done' || !visible) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fire('dismiss');
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [step, visible, fire]);

  const dismiss = useCallback(() => fire('dismiss'), [fire]);

  if (!visible || step === 'done') return null;

  if (step === 'edit-number') {
    return (
      <Coachmark
        eyebrow="first edit · 1 of 3"
        placement="editor"
        onDismiss={dismiss}
        body={
          <>
            See the glowing number? Change it — make it{' '}
            <span className="text-yellow-200">400</span>, make it{' '}
            <span className="text-yellow-200">4000</span> — and listen.
            The loop never stops for you.
          </>
        }
      />
    );
  }

  if (step === 'heard-change') {
    return (
      <Coachmark
        eyebrow="live-coding · 2 of 3"
        placement="editor"
        onDismiss={dismiss}
        body={
          <>
            Hear that? That&apos;s live-coding — every edit lands on the
            next cycle. No compile, no upload, no pause.
          </>
        }
        action={{ label: 'Next', onClick: () => fire('continue') }}
      />
    );
  }

  // step === 'meet-agent'
  return (
    <Coachmark
      eyebrow="the agent · 3 of 3"
      placement="agent"
      onDismiss={dismiss}
      body={
        <>
          The panel on the right is your bandmate. It edits this code
          with you — small changes, one line of why. Try it:
        </>
      }
      action={{
        label: `“${AGENT_INTRO_PROMPT}”`,
        onClick: () => {
          onSeedAgentPrompt(AGENT_INTRO_PROMPT);
          fire('complete');
        },
      }}
    />
  );
}
